import React, { useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { CloudUploadOutlined, InsertDriveFileOutlined } from '@mui/icons-material';

/** Accepted transcript/document extensions for sessions. */
export const SESSION_FILE_ACCEPT = '.txt,.md,.doc,.docx';

/**
 * Whether a file has an accepted session extension.
 * @param {File} file - File.
 * @returns {boolean} True when accepted.
 */
export function isValidSessionFile(file) {
  if (!file?.name) return false;
  const name = file.name.toLowerCase();
  return SESSION_FILE_ACCEPT.split(',').some((ext) => name.endsWith(ext));
}

/**
 * Keyboard-accessible drag-and-drop file picker.
 *
 * @param {object} props
 * @param {File|null} props.file - Selected file.
 * @param {(file: File) => void} props.onFile - Called with a valid file.
 * @param {boolean} [props.disabled=false] - Disable picking.
 * @param {boolean} [props.compact=false] - Smaller variant (batch rows).
 * @param {string} [props.accept] - Accept attribute.
 * @param {string} [props.label] - Accessible label.
 * @returns {JSX.Element}
 */
function FileDropZone({ file, onFile, disabled = false, compact = false, accept = SESSION_FILE_ACCEPT, label = 'Choose a file' }) {
  const inputRef = useRef(null);
  const dragDepth = useRef(0);
  const [isDragOver, setIsDragOver] = useState(false);
  const [rejected, setRejected] = useState('');

  const pick = (candidate) => {
    if (!candidate) return;
    if (!isValidSessionFile(candidate)) {
      setRejected(`${candidate.name} is not supported. Use ${accept.replace(/,/g, ', ')}.`);
      return;
    }
    setRejected('');
    onFile(candidate);
  };

  const stop = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Box>
      <Box
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-label={file ? `${label}: ${file.name}. Press to replace.` : label}
        aria-disabled={disabled}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragEnter={(e) => {
          stop(e);
          if (disabled) return;
          dragDepth.current += 1;
          setIsDragOver(true);
        }}
        onDragLeave={(e) => {
          stop(e);
          dragDepth.current = Math.max(0, dragDepth.current - 1);
          if (dragDepth.current === 0) setIsDragOver(false);
        }}
        onDragOver={stop}
        onDrop={(e) => {
          stop(e);
          dragDepth.current = 0;
          setIsDragOver(false);
          if (disabled) return;
          pick(e.dataTransfer?.files?.[0]);
        }}
        sx={{
          display: 'flex',
          flexDirection: compact ? 'row' : 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: compact ? 1 : 0.75,
          textAlign: compact ? 'left' : 'center',
          minHeight: compact ? 64 : 132,
          px: 2,
          py: compact ? 1.5 : 3,
          border: '1.5px dashed',
          borderColor: isDragOver ? 'primary.main' : 'var(--q-border-strong)',
          borderRadius: 'var(--q-radius-xl)',
          bgcolor: isDragOver ? 'var(--q-accent-tint)' : 'var(--q-bg-subtle)',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          transition: 'border-color 120ms ease, background-color 120ms ease',
          '&:hover': disabled ? undefined : { borderColor: 'primary.main' },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', outlineOffset: 2 },
        }}
      >
        {file ? (
          <InsertDriveFileOutlined sx={{ color: 'var(--q-accent-text)', fontSize: compact ? 22 : 30 }} />
        ) : (
          <CloudUploadOutlined sx={{ color: isDragOver ? 'primary.main' : 'text.secondary', fontSize: compact ? 22 : 30 }} />
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
            {file ? file.name : compact ? 'Drop a file or click' : 'Drop a file here or click to browse'}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {file ? `${(file.size / 1024).toFixed(1)} KB · click to replace` : `Supports ${accept.replace(/,/g, ', ')}`}
          </Typography>
        </Box>
        <input
          ref={inputRef}
          type="file"
          hidden
          accept={accept}
          disabled={disabled}
          onChange={(e) => {
            pick(e.target.files?.[0]);
            e.target.value = '';
          }}
        />
      </Box>
      {rejected && (
        <Typography variant="caption" color="error" role="alert" sx={{ display: 'block', mt: 0.5 }}>
          {rejected}
        </Typography>
      )}
    </Box>
  );
}

export default FileDropZone;
